import { LoaderCircle } from 'lucide-react';
import { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { InterfaceStatusType } from '@/types';

import InterfaceStatusCard from '@/components/map/interface-status-button/interface-status-card';
import InterfaceStatusTriggerContent from '@/components/map/interface-status-button/interface-status-trigger-content';
import StatusMessage from '@/components/shared/status-message';
import TooltipPopover from '@/components/ui/tooltip-popover';
import useBreakpoint from '@/hooks/use-break-point';
import useDrawerBottomOffset from '@/hooks/use-drawer-bottom-offset';
import useInterfaceCardHeight from '@/hooks/use-interface-card-height';
import { useIsLandscape } from '@/hooks/use-is-landscape';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import { cn } from '@/lib/utils';
import { ERROR_FEEDBACK, INTERFACE_STATUS_DOT } from '@/utils/constants';

interface InterfaceStatusButtonProps {
  isDrawerOpen?: boolean;
  isToastVisible?: boolean;
  drawerHeight?: number;
}

const InterfaceStatusButton: FC<InterfaceStatusButtonProps> = ({
  isDrawerOpen = false,
  isToastVisible = false,
  drawerHeight,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpoint('mobile');
  const landscape = useIsLandscape();

  useDrawerBottomOffset(containerRef, { drawerHeight, isDrawerOpen, isToastVisible, isMobile });
  useInterfaceCardHeight(popoverRef, drawerHeight, landscape, isOpen);

  const { data, isLoading, error, refetch } =
    useSocketSubscription<InterfaceStatusType>('interface_monitor');

  const dotColor = data?.status ? INTERFACE_STATUS_DOT[data.status] : null;

  const handleCloseAutoFocus = () => {
    popoverRef.current?.style.removeProperty('--interface-card-min-h');
    popoverRef.current?.style.removeProperty('--interface-card-max-h');
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <LoaderCircle className="size-12 animate-spin text-primary" />
        </div>
      );
    }

    if (!!error && !data) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <StatusMessage
            type="error"
            title={t(ERROR_FEEDBACK.general)}
            description={t(ERROR_FEEDBACK.interface_status.api)}
            showReloadButton
            onRetry={refetch}
            className="items-center text-center"
          />
        </div>
      );
    }

    if (!data) {
      return null;
    }

    return <InterfaceStatusCard data={data} />;
  };

  return (
    <div
      ref={containerRef}
      className={cn('fixed right-2.5 bottom-27.5 z-50 transition-all duration-300', {
        'bottom-[calc(var(--drawer-bottom-offset)+3rem)]':
          isMobile && isDrawerOpen && !isToastVisible,
        'bottom-[calc(var(--drawer-bottom-offset)+3.5rem)]': isToastVisible,
      })}
    >
      <TooltipPopover
        ref={popoverRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        tooltipLabel={t('interface_status.title')}
        tooltipSide="left"
        triggerContent={<InterfaceStatusTriggerContent dotColor={dotColor} />}
        triggerClassName="h-8 gap-2 p-2.5"
        triggerName="interface-status"
        contentSide="top"
        contentAlign="end"
        contentSideOffset={8}
        contentCollisionPadding={16}
        onCloseAutoFocus={handleCloseAutoFocus}
        contentClassName="w-75 p-4 rounded-lg border-none shadow-md flex flex-col gap-3 overflow-y-auto min-h-(--interface-card-min-h) max-h-(--interface-card-max-h) [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {renderContent()}
      </TooltipPopover>
    </div>
  );
};

export default InterfaceStatusButton;
